import { NextPageContext } from 'next'
import { Config } from '@chegg-wt/core/config'
import { createCheggInstance } from '@chegg/optimizely-sdk'
import { ReactSDKClient } from '@optimizely/react-sdk'
import { getOrSetVCookieServer } from '@chegg-wt/core/utils/getVCookie'
import {
  FORCE_EXP_KEY,
  AA_TEST,
  LOW_AD_CM,
  LOW_AD_EB,
  LOW_AD_CONTROL_CELL,
  LOW_AD_DEFAULT_CM,
  LOW_AD_DEFAULT_EB,
  LOW_AD_VARIATIONS,
  INTERSTITIAL_AD_EB,
  INTERSTITIAL_AD_CTFM,
  INTERSTITIAL_AD_CTFM_EB_CONTROL_CELL,
  INTERSTITIAL_AD_CTFM_EB_VARIATIONS,
} from '@chegg-wt/core/state/optimizely/constants'
import { BackendOpts } from '@chegg-wt/core/state/featureFlags/types'
import { trackWithoutThrow } from './errors'
import { getBackendOpts, Cookies } from './cookies'

let optimizely: ReactSDKClient

export const getBackendOptsSafe = (ctx: NextPageContext) => {
  try {
    return getBackendOpts(ctx)
  } catch (e) {
    trackWithoutThrow(e)
    return {}
  }
}

type NextPageContextWithBackendsOpts = NextPageContext & {
  req: { backendOpts: BackendOpts; cookies: { [key: string]: string } }
}

export const getBackendOptsWithOptimizely = async (
  ctx: NextPageContextWithBackendsOpts
) => {
  if (ctx?.req?.backendOpts) {
    return Promise.resolve(ctx.req.backendOpts)
  }

  const backendOpts: BackendOpts = {}
  const { tokenSiteCode } = Config
  const userRole = ctx.req.cookies[Cookies.UserRole]

  if (!optimizely) {
    optimizely = createCheggInstance({
      sdkKey: Config.optimizelySdkKey,
      cookieDomain: Config.cookieDomain,
    }).optimizely
    await optimizely.onReady()
  }
  const vCookie = getOrSetVCookieServer(Config.cookieDomain, ctx)

  const forceInterstitialAdEbExp = `${FORCE_EXP_KEY}interstitial-ad-eb-experiment`
  if (ctx.query[forceInterstitialAdEbExp]) {
    const variation = ctx.query[forceInterstitialAdEbExp]
    backendOpts.adConfigVersion =
      typeof variation === 'string' &&
      (INTERSTITIAL_AD_CTFM_EB_VARIATIONS[variation] ||
        INTERSTITIAL_AD_CTFM_EB_CONTROL_CELL)
  } else if (
    optimizely.isFeatureEnabled(INTERSTITIAL_AD_EB, vCookie, {
      siteName: tokenSiteCode,
      userRole,
    })
  ) {
    backendOpts.adConfigVersion = optimizely.getFeatureVariableString(
      INTERSTITIAL_AD_EB,
      'adConfigVersion',
      vCookie,
      {
        siteName: tokenSiteCode,
        userRole,
      }
    )
  }

  const forceInterstitialAdCtfmExp = `${FORCE_EXP_KEY}interstitial-ad-ctfm-experiment`
  if (ctx.query[forceInterstitialAdCtfmExp]) {
    const variation = ctx.query[forceInterstitialAdCtfmExp]
    backendOpts.adConfigVersion =
      typeof variation === 'string' &&
      (INTERSTITIAL_AD_CTFM_EB_VARIATIONS[variation] ||
        INTERSTITIAL_AD_CTFM_EB_CONTROL_CELL)
  } else if (
    optimizely.isFeatureEnabled(INTERSTITIAL_AD_CTFM, vCookie, {
      siteName: tokenSiteCode,
      userRole,
    })
  ) {
    backendOpts.adConfigVersion = optimizely.getFeatureVariableString(
      INTERSTITIAL_AD_CTFM,
      'adConfigVersion',
      vCookie,
      {
        siteName: tokenSiteCode,
        userRole,
      }
    )
  }

  // setting force experiments for low ad test in "if" statement.
  const forceLowAdEbExp = `${FORCE_EXP_KEY}low-ad-eb-exp`
  if (ctx.query[forceLowAdEbExp]) {
    const variation = ctx.query[forceLowAdEbExp]
    backendOpts.adConfigVersion =
      typeof variation === 'string' &&
      (LOW_AD_VARIATIONS[variation] || LOW_AD_CONTROL_CELL)
  } else if (
    optimizely.isFeatureEnabled(LOW_AD_EB, vCookie, { siteName: tokenSiteCode })
  ) {
    const adConfigVersion = optimizely.getFeatureVariableString(
      LOW_AD_EB,
      'adConfigVersion',
      vCookie,
      {
        // need to pass siteName explicitly to match Audience of Optimizely Experiment
        siteName: tokenSiteCode,
      }
    )
    // skipping default value setting backendOpts only for optimizely experiment variations
    if (adConfigVersion !== LOW_AD_DEFAULT_EB) {
      backendOpts.adConfigVersion = adConfigVersion
    }
  }

  // setting force experiments for low ad test in "if" statement.
  const forceLowAdCmExp = `${FORCE_EXP_KEY}low-ad-cm-exp`
  if (ctx.query[forceLowAdCmExp]) {
    const variation = ctx.query[forceLowAdCmExp]
    backendOpts.adConfigVersion =
      typeof variation === 'string' &&
      (LOW_AD_VARIATIONS[variation] || LOW_AD_CONTROL_CELL)
  } else if (
    optimizely.isFeatureEnabled(LOW_AD_CM, vCookie, { siteName: tokenSiteCode })
  ) {
    const adConfigVersion = optimizely.getFeatureVariableString(
      LOW_AD_CM,
      'adConfigVersion',
      vCookie,
      {
        // need to pass siteName explicitly to match Audience of Optimizely Experiment
        siteName: tokenSiteCode,
      }
    )
    // skipping default value setting backendOpts only for optimizely experiment variations
    if (adConfigVersion !== LOW_AD_DEFAULT_CM) {
      backendOpts.adConfigVersion = adConfigVersion
    }
  }

  if (optimizely.isFeatureEnabled(AA_TEST, vCookie)) {
    const adConfigVersion = optimizely.getFeatureVariableString(
      AA_TEST,
      'adConfigVersion',
      vCookie
    )
    backendOpts.adConfigVersion = adConfigVersion
  }

  const mergedBackendOpts = Object.assign(getBackendOpts(ctx), backendOpts) as BackendOpts
  if (ctx?.req) {
    ctx.req.backendOpts = mergedBackendOpts
  }
  return mergedBackendOpts
}

export const getBackendOptsWithOptimizelySafe = async (
  ctx: NextPageContextWithBackendsOpts
): Promise<BackendOpts> => {
  try {
    return getBackendOptsWithOptimizely(ctx)
  } catch (e) {
    trackWithoutThrow(e)
    return {}
  }
}
